/**
 * 🎯 ZONE-BASED HOOKS FOR VOICEACTOR WORKSHEET
 * 
 * Clean, isolated hooks for each zone that prevent cross-zone contamination.
 * Modeled after bookWorksheet/hooks/core/useZoneHooks.js with VoiceActor adaptations.
 */

import { useSelector, useDispatch } from 'react-redux';
import { useMemo } from 'react';
import {
  // Selectors (will be created in next step)
  selectActorZoneData,
  selectPortfolioZoneData,
  selectCharacterZoneData,
  selectVariationZoneData,
  selectSharedDataForComponents,
  selectSharedDataSelectedActorId,
  selectSharedDataSelectedPortfolioId,
  selectSharedDataSelectedCharacterId,
  selectCurrentActor,
  selectCurrentPortfolio
} from '../../selectors/zoneSelectors';

import {
  // Actions
  setActorSelection,
  autoSelectFirstPortfolio,
  setPortfolioSelection,
  setCharacterSelection,
  setVariationSelection,
  updateVersionField,
  setActiveVersionFromSamples,
  setEditMode,
  clearUnsavedChanges,
  setSubmitting,
  clearActorZone,
  clearPortfolioZone,
  clearCharacterZone,
  clearVariationZone,

  // Thunks
  fetchActors,
  fetchActorProfile,
  createActor,
  updateActor,
  deleteActor,
  fetchPortfolios,
  fetchPortfolioProfile,
  createPortfolio,
  updatePortfolio,
  deletePortfolio,
  fetchCharacters,
  // fetchCharacter,
  fetchVariations,
  fetchVariationCounts,
  loadVariationForEdit,
  createCharacter,
  updateCharacter,
  deleteCharacter,
  createVariation,
  updateVariation,
  deleteVariation,
  deleteVersion,
  loadDefaultVersion,
  saveVariation
} from '../../slices/voiceActorDataSlice';

// ===== ACTOR ZONE HOOK (PURE) =====
export const useActorZone = () => {
  const dispatch = useDispatch();
  const zoneData = useSelector(selectActorZoneData);

  const actions = useMemo(() => ({
    // Selection actions
    selectActor: (actorId) => {
      console.log('[ACTOR_ZONE] Selecting actor:', actorId);
      dispatch(setActorSelection(actorId));
    },

    // Data fetching actions
    fetchActors: () => {
      console.log('[ACTOR_ZONE] Fetching actors');
      return dispatch(fetchActors());
    },

    fetchActorProfile: (actorId) => {
      console.log('[ACTOR_ZONE] Fetching actor profile:', actorId);
      return dispatch(fetchActorProfile(actorId));
    },

    // Actor CRUD actions
    createActor: (data) => {
      console.log('[ACTOR_ZONE] Creating actor:', data);
      return dispatch(createActor(data));
    },

    updateActor: ({ actorId, data }) => {
      console.log('[ACTOR_ZONE] Updating actor:', actorId);
      return dispatch(updateActor({ actorId, data }));
    },

    deleteActor: (actorId) => {
      console.log('[ACTOR_ZONE] Deleting actor:', actorId);
      return dispatch(deleteActor(actorId));
    },

    // Zone management
    clearZone: () => {
      console.log('[ACTOR_ZONE] Clearing zone');
      dispatch(clearActorZone());
    }
  }), [dispatch]);

  return useMemo(() => ({
    ...zoneData,
    actions
  }), [zoneData, actions]);
};

// ===== PORTFOLIO ZONE HOOK (PURE) =====
export const usePortfolioZone = () => {
  const dispatch = useDispatch();
  const zoneData = useSelector(selectPortfolioZoneData);

  const actions = useMemo(() => ({
    // Selection actions
    selectPortfolio: (portfolioId) => {
      console.log('[PORTFOLIO_ZONE] Selecting portfolio:', portfolioId);
      dispatch(setPortfolioSelection(portfolioId));
    },

    // Data fetching actions
    fetchPortfolios: (actorId) => {
      console.log('[PORTFOLIO_ZONE] Fetching portfolios for actor:', actorId);
      return dispatch(fetchPortfolios(actorId));
    },

    fetchPortfolioProfile: (portfolioId) => {
      console.log('[PORTFOLIO_ZONE] Fetching portfolio profile:', portfolioId);
      return dispatch(fetchPortfolioProfile(portfolioId));
    },

    // Portfolio CRUD actions
    createPortfolio: ({ actorId, data }) => {
      console.log('[PORTFOLIO_ZONE] Creating portfolio for actor:', actorId);
      return dispatch(createPortfolio({ actorId, data }));
    },

    updatePortfolio: ({ portfolioId, data }) => {
      console.log('[PORTFOLIO_ZONE] Updating portfolio:', portfolioId);
      return dispatch(updatePortfolio({ portfolioId, data }));
    },

    deletePortfolio: (portfolioId) => {
      console.log('[PORTFOLIO_ZONE] Deleting portfolio:', portfolioId);
      return dispatch(deletePortfolio(portfolioId));
    },

    // Zone management
    clearZone: () => {
      console.log('[PORTFOLIO_ZONE] Clearing zone');
      dispatch(clearPortfolioZone());
    }
  }), [dispatch]);

  return useMemo(() => ({
    ...zoneData,
    actions
  }), [zoneData, actions]);
};

// ===== CHARACTER ZONE HOOK (PURE) - EXACT SAME as BookWorksheet =====
export const useCharacterZone = () => {
  const dispatch = useDispatch();
  const zoneData = useSelector(selectCharacterZoneData);

  const actions = useMemo(() => ({
    // Selection actions
    selectCharacter: (characterId) => {
      console.log('[CHARACTER_ZONE] Selecting character:', characterId);
      dispatch(setCharacterSelection(characterId));
    },

    // Data fetching actions
    fetchCharacters: (portfolioId) => { // portfolioId instead of bookId
      console.log('[CHARACTER_ZONE] Fetching characters for portfolio:', portfolioId);
      return dispatch(fetchCharacters(portfolioId));
    },

    // Character CRUD actions
    createCharacter: (portfolioId, data) => { // portfolioId instead of bookId
      console.log('[CHARACTER_ZONE] Creating character for portfolio:', portfolioId);
      return dispatch(createCharacter({ portfolioId, data }));
    },

    updateCharacter: (characterId, portfolioId, data) => { // portfolioId instead of bookId
      console.log('[CHARACTER_ZONE] Updating character:', characterId);
      return dispatch(updateCharacter({ characterId, portfolioId, data }));
    },

    deleteCharacter: (params) => {
      console.log('[CHARACTER_ZONE] Deleting character:', params);
      return dispatch(deleteCharacter(params));
    },

    // Zone management
    clearZone: () => {
      console.log('[CHARACTER_ZONE] Clearing zone');
      dispatch(clearCharacterZone());
    }
  }), [dispatch]);

  return useMemo(() => ({
    ...zoneData,
    actions
  }), [zoneData, actions]);
};

// ===== VARIATION ZONE HOOK (PURE) - EXACT SAME as BookWorksheet =====
export const useVariationZone = () => {
  const dispatch = useDispatch();
  const zoneData = useSelector(selectVariationZoneData);

  const actions = useMemo(() => ({
    // Selection actions
    selectVariation: (variationId) => {
      console.log('[VARIATION_ZONE] Selecting variation:', variationId);
      dispatch(setVariationSelection(variationId));
    },

    // Data fetching actions
    // In useVariationZone()
    fetchVariations: (characterId, portfolioId) => {
      console.log('[VARIATION_ZONE] Fetching variations for character:', characterId);
      return dispatch(fetchVariations({ characterId, portfolioId }));
    },


    fetchVariationCounts: (characterId) => {
      console.log('[VARIATION_ZONE] Fetching variation counts for character:', characterId);
      return dispatch(fetchVariationCounts(characterId));
    },

    loadVariationForEdit: ({ variationId, characterId, actorId, portfolioId }) => { // actorId, portfolioId instead of litGroupId, bookId
      console.log('[VARIATION_ZONE] Loading variation for edit:', variationId);
      return dispatch(loadVariationForEdit({ variationId, characterId, actorId, portfolioId }));
    },

    loadDefaultVersion: (characterId) => {
      console.log('[VARIATION_ZONE] Loading default version for character:', characterId);
      return dispatch(loadDefaultVersion({ characterId }));
    },

    // Version management
    updateVersionField: (field, value) => {
      console.log('[VARIATION_ZONE] Updating version field:', field, '=', value);
      dispatch(updateVersionField({ field, value }));
    },

    setActiveVersionFromSamples: (sampleUuid) => {
      console.log('[VARIATION_ZONE] Setting active version from samples:', sampleUuid);
      dispatch(setActiveVersionFromSamples(sampleUuid));
    },

    // Edit mode management
    setEditMode: (mode) => {
      console.log('[VARIATION_ZONE] Setting edit mode:', mode);
      dispatch(setEditMode(mode));
    },

    clearUnsavedChanges: () => {
      console.log('[VARIATION_ZONE] Clearing unsaved changes');
      dispatch(clearUnsavedChanges());
    },

    setSubmitting: (isSubmitting) => {
      dispatch(setSubmitting(isSubmitting));
    },

    // Variation CRUD actions
    createVariation: (characterId, data) => {
      console.log('[VARIATION_ZONE] Creating variation for character:', characterId);
      return dispatch(createVariation({ characterId, data }));
    },

    updateVariation: (variationId, data) => {
      console.log('[VARIATION_ZONE] Updating variation:', variationId);
      return dispatch(updateVariation({ variationId, data }));
    },

    deleteVariation: (variationId, options = {}) => {
      console.log('[VARIATION_ZONE] Deleting variation:', variationId, 'with options:', options);
      return dispatch(deleteVariation({ variationId, options }));
    },

    deleteVersion: (versionId, variationId) => {
      console.log('[VARIATION_ZONE] Deleting version:', versionId);
      return dispatch(deleteVersion({ versionId, variationId }));
    },

    saveVariation: (variationData, versionData, editMode, characterId) => {
      console.log('[VARIATION_ZONE] Saving variation in mode:', editMode);
      return dispatch(saveVariation({ variationData, versionData, editMode, characterId }));
    },

    // Zone management
    clearZone: () => {
      console.log('[VARIATION_ZONE] Clearing zone');
      dispatch(clearVariationZone());
    }
  }), [dispatch]);

  return useMemo(() => ({
    ...zoneData,
    actions
  }), [zoneData, actions]);
};

// ===== SHARED DATA HOOK (READ-ONLY) =====
export const useSharedData = () => {
  const dispatch = useDispatch();
  const sharedData = useSelector(selectSharedDataForComponents);

  const actions = useMemo(() => ({
    // Selection actions
    setActorSelection: (actorId) => {
      console.log('[SHARED_DATA] Setting actor selection:', actorId);
      dispatch(setActorSelection(actorId));
    },

    setPortfolioSelection: (portfolioId) => {
      console.log('[SHARED_DATA] Setting portfolio selection:', portfolioId);
      dispatch(setPortfolioSelection(portfolioId));
    },

    // Data fetching actions
    fetchActors: () => {
      console.log('[SHARED_DATA] Fetching actors');
      return dispatch(fetchActors());
    },

    fetchActorProfile: (actorId) => {
      console.log('[SHARED_DATA] Fetching actor profile:', actorId);
      return dispatch(fetchActorProfile(actorId));
    },

    fetchPortfolios: async (actorId) => {
      console.log('[SHARED_DATA] Fetching portfolios for actor:', actorId);
      const result = await dispatch(fetchPortfolios(actorId));
      // Auto-select first portfolio after fetching
      dispatch(autoSelectFirstPortfolio());
      return result;
    },

    fetchPortfolioProfile: (portfolioId) => {
      console.log('[SHARED_DATA] Fetching portfolio profile:', portfolioId);
      return dispatch(fetchPortfolioProfile(portfolioId));
    }
  }), [dispatch]);

  return useMemo(() => ({
    ...sharedData,
    actions
  }), [sharedData, actions]);
};

// ===== COMPOSITE HOOKS FOR SPECIFIC USE CASES =====

// Hook for components that need actor management functionality
export const useActorManagement = () => {
  const actorZone = useActorZone();
  const sharedData = useSharedData();

  const compositeActions = useMemo(() => ({
    // Initialize actor management
    initializeActorManagement: async () => {
      console.log('[ACTOR_MGMT] Initializing actor management');
      return actorZone.actions.fetchActors();
    },

    // Select and load actor
    selectAndLoadActor: async (actorId) => {
      console.log('[ACTOR_MGMT] Selecting and loading actor:', actorId);
      sharedData.actions.setActorSelection(actorId);
      return sharedData.actions.fetchActorProfile(actorId);
    }
  }), [actorZone.actions, sharedData.actions]);

  return useMemo(() => ({
    // Actor zone data
    actors: actorZone.actors,
    selectedActor: actorZone.selectedActor,
    loading: actorZone.loading,
    error: actorZone.error,

    // Shared data needed for actor management
    actorProfiles: sharedData.actorProfiles,
    selections: sharedData.selections,

    // Combined actions
    actions: {
      ...actorZone.actions,
      ...compositeActions
    }
  }), [actorZone, sharedData, compositeActions]);
};

// Hook for components that need portfolio management functionality
export const usePortfolioManagement = () => {
  const portfolioZone = usePortfolioZone();
  const sharedData = useSharedData();
  // const selectedActorId = useSelector(selectSelectedActorId);

  const compositeActions = useMemo(() => ({
    // Initialize portfolio management for an actor
    initializeForActor: async (actorId) => {
      console.log('[PORTFOLIO_MGMT] Initializing for actor:', actorId);
      sharedData.actions.setActorSelection(actorId);
      return portfolioZone.actions.fetchPortfolios(actorId);
    },

    // Select and load portfolio
    selectAndLoadPortfolio: async (portfolioId) => {
      console.log('[PORTFOLIO_MGMT] Selecting and loading portfolio:', portfolioId);
      sharedData.actions.setPortfolioSelection(portfolioId);
      return sharedData.actions.fetchPortfolioProfile(portfolioId);
    }
  }), [portfolioZone.actions, sharedData.actions]);

  return useMemo(() => ({
    // Portfolio zone data
    portfolios: portfolioZone.portfolios,
    activePortfolio: portfolioZone.activePortfolio,
    loading: portfolioZone.loading,
    error: portfolioZone.error,

    // Shared data needed for portfolio management
    actors: sharedData.actors,
    portfolioProfiles: sharedData.portfolioProfiles,
    selections: sharedData.selections,

    // Combined actions
    actions: {
      ...portfolioZone.actions,
      ...compositeActions
    }
  }), [portfolioZone, sharedData, compositeActions]);
};

// Hook for components that need character management functionality
export const useCharacterManagement = () => {
  const characterZone = useCharacterZone();
  const sharedData = useSharedData();
  const selectedPortfolioId = useSelector(selectSharedDataSelectedPortfolioId);

  const compositeActions = useMemo(() => ({
    // Initialize character management for a portfolio
    initializeForPortfolio: async (portfolioId) => {
      console.log('[CHARACTER_MGMT] Initializing for portfolio:', portfolioId);
      sharedData.actions.setPortfolioSelection(portfolioId);
      return characterZone.actions.fetchCharacters(portfolioId);
    },

    // Select and load character
    selectAndLoadCharacter: async (characterId) => {
      console.log('[CHARACTER_MGMT] Selecting and loading character:', characterId);
      characterZone.actions.selectCharacter(characterId);
    },

    // Create new character
    createNewCharacter: async (characterData) => {
      if (!selectedPortfolioId) {
        throw new Error('No portfolio selected for character creation');
      }
      console.log('[CHARACTER_MGMT] Creating new character');
      return characterZone.actions.createCharacter(selectedPortfolioId, characterData);
    }
  }), [characterZone.actions, sharedData.actions, selectedPortfolioId]);

  return useMemo(() => ({
    // Character zone data
    characters: characterZone.characters,
    selectedCharacter: characterZone.selectedCharacter,
    loading: characterZone.loading,
    error: characterZone.error,

    // Shared data needed for character management
    actors: sharedData.actors,
    portfolios: sharedData.portfolios,
    selections: sharedData.selections,

    // Combined actions
    actions: {
      ...characterZone.actions,
      ...compositeActions
    }
  }), [characterZone, sharedData, compositeActions]);
};

// Hook for components that need variation management functionality
export const useVariationManagement = () => {
  const variationZone = useVariationZone();
  const selectedCharacterId = useSelector(selectSharedDataSelectedCharacterId);
  const selectedActorId = useSelector(selectSharedDataSelectedActorId);
  const selectedPortfolioId = useSelector(selectSharedDataSelectedPortfolioId);

  const compositeActions = useMemo(() => ({
    // Initialize variation management for a character
    initializeForCharacter: async (characterId) => {
      console.log('[VARIATION_MGMT] Initializing for character:', characterId);

      if (!selectedPortfolioId) {
        throw new Error('No portfolio selected for fetching variations');
      }

      return variationZone.actions.fetchVariations(characterId, selectedPortfolioId);
    },

    // Load variation for editing
    loadForEdit: async (variationId) => {
      if (!selectedCharacterId || !selectedActorId || !selectedPortfolioId) {
        throw new Error('Missing required selections for variation editing');
      }
      console.log('[VARIATION_MGMT] Loading variation for edit:', variationId);
      return variationZone.actions.loadVariationForEdit({
        variationId,
        selectedCharacterId,
        selectedActorId,
        selectedPortfolioId
      }
      );
    },

    // Create new variation
    createNewVariation: async (variationData) => {
      if (!selectedCharacterId) {
        throw new Error('No character selected for variation creation');
      }
      console.log('[VARIATION_MGMT] Creating new variation');
      return variationZone.actions.createVariation(selectedCharacterId, variationData);
    },

    // Save current variation
    saveCurrentVariation: async () => {
      if (!variationZone.activeVariation) {
        throw new Error('No active variation to save');
      }
      console.log('[VARIATION_MGMT] Saving current variation');
      variationZone.actions.setSubmitting(true);
      try {
        const result = await variationZone.actions.saveVariation(
          variationZone.activeVariation,
          variationZone.activeVersion,
          variationZone.editMode,
          selectedCharacterId
        );
        variationZone.actions.clearUnsavedChanges();
        return result;
      } finally {
        variationZone.actions.setSubmitting(false);
      }
    }
  }), [
    variationZone.actions,
    variationZone.activeVariation,
    variationZone.activeVersion,
    variationZone.editMode,
    selectedCharacterId,
    selectedActorId,
    selectedPortfolioId
  ]);

  return useMemo(() => ({
    // Variation zone data
    variations: variationZone.variations,
    activeVariation: variationZone.activeVariation,
    activeVersion: variationZone.activeVersion,
    samples: variationZone.samples,
    variationCounts: variationZone.variationCounts,
    loading: variationZone.loading,
    error: variationZone.error,
    editMode: variationZone.editMode,
    hasUnsavedChanges: variationZone.hasUnsavedChanges,
    isSubmitting: variationZone.isSubmitting,

    // Context data
    characterId: variationZone.characterId,

    // Combined actions
    actions: {
      ...variationZone.actions,
      ...compositeActions
    }
  }), [variationZone, compositeActions]);
};

// ===== LEGACY COMPATIBILITY HOOK =====
// This hook provides backward compatibility during migration
export const useReduxSelectorsZoneBasedNew = () => {
  const actorZone = useActorZone();
  const portfolioZone = usePortfolioZone();
  const characterZone = useCharacterZone();
  const variationZone = useVariationZone();
  const sharedData = useSharedData();

  // Get selected entities
  const selectedActor = useSelector(selectCurrentActor);
  const selectedPortfolio = useSelector(selectCurrentPortfolio);
  const actorId = useSelector(selectSharedDataSelectedActorId);
  const portfolioId = useSelector(selectSharedDataSelectedPortfolioId);
  // const actorsLoading = sharedData.loading?.actors;
  // const actorsError = sharedData.errors?.actors;
  // const actorsLastUpdated = sharedData.lastUpdated?.actors;

  // Return data in the format expected by existing components
  return useMemo(() => ({
    // Entity IDs (mirror BookWorksheet pattern)
    actorId,           // instead of litGroupId
    portfolioId,       // instead of bookId
    characterId: sharedData.selections?.characterId,

    // Actor data (mirror BookWorksheet's litGroup data)
    selectedActor,
    actors: sharedData.actors,

    // Portfolio data (mirror BookWorksheet's book data)
    selectedPortfolio,
    portfolios: sharedData.portfolios,
    portfoliosLoading: sharedData.loading?.portfolios,
    portfoliosError: sharedData.errors?.portfolios,
    portfoliosLastUpdated: sharedData.lastUpdated?.portfolios,

    // Character data (same as BookWorksheet)
    characters: characterZone.characters,
    selectedCharacter: characterZone.selectedCharacter,
    charactersLoading: characterZone.loading,
    charactersError: characterZone.error,
    charactersLastUpdated: characterZone.lastUpdated,

    // Variation data (same as BookWorksheet)
    variations: variationZone.variations,
    activeVariation: variationZone.activeVariation,
    activeVersion: variationZone.activeVersion,
    samples: variationZone.samples,

    // Shared data
    selections: sharedData.selections,

    // Loading states (mirror BookWorksheet structure)
    loading: {
      actors: actorZone.loading,
      portfolios: portfolioZone.loading,
      characters: characterZone.loading,
      variations: variationZone.loading
    },

    // Error states (mirror BookWorksheet structure)
    errors: {
      actors: actorZone.error,
      portfolios: portfolioZone.error,
      characters: characterZone.error,
      variations: variationZone.error
    },

    // Actions (combined for compatibility)
    actions: {
      ...actorZone.actions,
      ...portfolioZone.actions,
      ...characterZone.actions,
      ...variationZone.actions,
      ...sharedData.actions
    }
  }), [actorZone, portfolioZone, characterZone, variationZone, sharedData, selectedActor, selectedPortfolio, actorId, portfolioId]);
};
